import { useState, useCallback, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SmilePlus, X, Tag } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";

const EMOJI_OPTIONS = ['👍', '👏', '✅', '💪', '❌', '😂', '😊', '😉', '😢', '🔥', '🙏'] as const;

interface ReactionEntryWithReactor {
  id: string;
  targetType: string;
  targetId: string;
  reactorId: number;
  type: 'emoji' | 'text';
  content: string;
  createdAt: Date | string;
  reactor?: {
    id: number;
    username?: string;
    firstName?: string | null;
    lastName?: string | null;
    shortCode?: string | null;
    colorCode?: string | null;
  };
}

interface ReactionGroup {
  content: string;
  type: 'emoji' | 'text';
  count: number;
  reactors: {
    id: number;
    name: string;
    color?: string;
  }[];
  hasCurrentUser: boolean;
}

function groupReactions(reactions: ReactionEntryWithReactor[], currentUserId: number): ReactionGroup[] {
  const groups = new Map<string, ReactionGroup>();
  
  reactions.forEach(r => {
    const key = `${r.type}:${r.content}`;
    if (!groups.has(key)) {
      groups.set(key, {
        content: r.content,
        type: r.type,
        count: 0,
        reactors: [],
        hasCurrentUser: false,
      });
    }
    const group = groups.get(key)!;
    group.count++;
    if (r.reactor) {
      const name = r.reactor.firstName && r.reactor.lastName 
        ? `${r.reactor.firstName} ${r.reactor.lastName}` 
        : (r.reactor.username || r.reactor.firstName || r.reactor.shortCode || 'User');
      group.reactors.push({
        id: r.reactor.id,
        name,
        color: r.reactor.colorCode || undefined,
      });
    }
    if (r.reactorId === currentUserId) {
      group.hasCurrentUser = true;
    }
  });
  
  return Array.from(groups.values()).sort((a, b) => {
    if (a.type !== b.type) return a.type === 'emoji' ? -1 : 1;
    return b.count - a.count;
  });
}

interface ReactionBarProps {
  targetType: string;
  targetId: string;
  onReact?: (type: 'emoji' | 'text', content: string) => void;
  channelId?: string;
  className?: string;
}

export function ReactionBar({ targetType, targetId, onReact, channelId, className }: ReactionBarProps) {
  const [textInput, setTextInput] = useState("");
  const [showTextInput, setShowTextInput] = useState(false);
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const addReactionMutation = useMutation({
    mutationFn: async ({ type, content }: { type: 'emoji' | 'text'; content: string }) => {
      return apiRequest('POST', '/api/reactions', { targetType, targetId, type, content });
    },
    onSuccess: () => {
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: ['/api/chat/channels', channelId, 'messages'] });
      }
    },
  });

  const handleEmojiClick = useCallback((emoji: string) => {
    addReactionMutation.mutate({ type: 'emoji', content: emoji });
    onReact?.('emoji', emoji);
    setOpen(false);
  }, [addReactionMutation, onReact]);

  const handleTextSubmit = useCallback(() => {
    const trimmed = textInput.trim();
    if (trimmed && trimmed.length <= 20) {
      addReactionMutation.mutate({ type: 'text', content: trimmed });
      onReact?.('text', trimmed);
      setTextInput("");
      setShowTextInput(false);
      setOpen(false);
    }
  }, [textInput, addReactionMutation, onReact]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn(
            "h-6 w-6 rounded-full bg-slate-700/90 hover:bg-slate-600 shadow-sm border border-white/20 transition-colors backdrop-blur-sm",
            className
          )}
        >
          <SmilePlus className="h-3.5 w-3.5 text-white/90" />
        </Button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-auto p-2" 
        side="top" 
        align="center"
        sideOffset={4}
      >
        {!showTextInput ? (
          <div className="flex flex-col gap-2">
            <div className="flex gap-1">
              {EMOJI_OPTIONS.map((emoji) => (
                <button
                  key={emoji}
                  onClick={() => handleEmojiClick(emoji)}
                  className="text-lg hover:scale-125 transition-transform p-1 rounded hover:bg-muted"
                  disabled={addReactionMutation.isPending}
                >
                  {emoji}
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTextInput(true)}
              className="text-xs gap-1 h-6"
            >
              <Tag className="h-3 w-3" />
              Add text tag
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <Input
              value={textInput}
              onChange={(e) => setTextInput(e.target.value.slice(0, 20))}
              placeholder="Tag (max 20 chars)"
              className="h-7 text-xs w-32"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleTextSubmit();
                if (e.key === 'Escape') {
                  setShowTextInput(false);
                  setTextInput("");
                }
              }}
              autoFocus
            />
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={handleTextSubmit}>
              <SmilePlus className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => {
              setShowTextInput(false);
              setTextInput("");
            }}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

interface ReactionBubblesProps {
  reactions: ReactionEntryWithReactor[];
  currentUserId: number;
  targetType: string;
  targetId: string;
  channelId?: string;
  isOwn?: boolean;
}

export function ReactionBubbles({ reactions, currentUserId, targetType, targetId, channelId, isOwn }: ReactionBubblesProps) {
  const queryClient = useQueryClient();
  
  const grouped = useMemo(() => groupReactions(reactions, currentUserId), [reactions, currentUserId]);
  
  const toggleReactionMutation = useMutation({
    mutationFn: async ({ type, content, hasIt }: { type: 'emoji' | 'text'; content: string; hasIt: boolean }) => {
      if (hasIt) {
        return apiRequest('DELETE', '/api/reactions', { targetType, targetId, type, content });
      } else {
        return apiRequest('POST', '/api/reactions', { targetType, targetId, type, content });
      }
    },
    onSuccess: () => {
      if (channelId) {
        queryClient.invalidateQueries({ queryKey: ['/api/chat/channels', channelId, 'messages'] });
      }
    },
  });
  
  if (grouped.length === 0) return null;
  
  return (
    <div className={cn(
      "flex flex-wrap gap-0.5",
      isOwn ? "justify-end" : "justify-start"
    )}>
      {grouped.map((group) => (
        <button
          key={`${group.type}:${group.content}`}
          onClick={() => toggleReactionMutation.mutate({ 
            type: group.type, 
            content: group.content, 
            hasIt: group.hasCurrentUser 
          })}
          disabled={toggleReactionMutation.isPending}
          className={cn(
            "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] transition-colors",
            group.hasCurrentUser 
              ? "bg-primary/20 border border-primary/50 text-primary" 
              : "bg-muted/80 border border-border hover:bg-muted",
            group.type === 'text' && "font-medium"
          )}
          title={group.reactors.map(r => r.name).join(', ')}
        >
          {group.type === 'emoji' ? (
            <span className="text-xs">{group.content}</span>
          ) : (
            <span className="text-[10px]">#{group.content}</span>
          )}
          {group.count > 1 && (
            <span className="text-[9px] text-muted-foreground">{group.count}</span>
          )}
        </button>
      ))}
    </div>
  );
}
